import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Megaphone, Lightning, Heart, Fire, ArrowUp, Compass, Trophy,
  Play, Pause, Eye, PaperPlaneTilt, Plus, X, Users, ChartLineUp,
  Clock, CheckCircle, WarningCircle, Trash, CaretDown
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_ICONS = {
  come_back_and_play: Heart,
  hot_streak: Fire,
  level_up_soon: ArrowUp,
  weekly_discovery: Compass,
  tournament_reminder: Trophy,
};

const TARGETING_TYPES = [
  { value: 'inactive_users', label: 'Inactive Users', desc: 'Users who haven\'t played recently' },
  { value: 'win_streak', label: 'Win Streak', desc: 'Users on a winning streak' },
  { value: 'level_up_proximity', label: 'Close to Level Up', desc: 'Users near their next level' },
  { value: 'active_with_preferences', label: 'Active w/ Preferences', desc: 'Users with preferred games set' },
  { value: 'all_users', label: 'All Users', desc: 'Broadcast to all non-admin users' },
];

const CATEGORIES = [
  'reminder_alerts', 'rivalry_prompts', 'achievement_updates',
  'discovery_recommendations', 'tournament_updates', 'session_updates',
  'shop_events', 'favorite_games', 'premium_or_plan',
];

function StatCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon size={14} weight="duotone" style={{ color: accent }} />
        </div>
        <span className="text-[11px] text-[#555] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-[#444] mt-0.5">{sub}</p>}
    </div>
  );
}

function TemplateCard({ template, onPreview, onExecute, previewing, executing }) {
  const Icon = TEMPLATE_ICONS[template.template_id] || Megaphone;
  const [previewData, setPreviewData] = useState(null);

  const handlePreview = async () => {
    const data = await onPreview(template.template_id);
    setPreviewData(data);
  };

  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4 hover:border-white/[0.1] transition-colors" data-testid={`template-${template.template_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${template.color}20` }}>
            <Icon size={16} weight="duotone" style={{ color: template.color }} />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white">{template.name}</h3>
            <p className="text-[11px] text-[#555]">{template.description}</p>
          </div>
        </div>
        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-medium"
          style={{ color: template.color, borderColor: `${template.color}30`, background: `${template.color}08` }}>
          {template.targeting.type.replace(/_/g, ' ')}
        </span>
      </div>

      <div className="bg-[#07070c] rounded-md p-2.5 mb-3 border border-white/[0.04]">
        <p className="text-[11px] text-[#888] font-medium">{template.title_template}</p>
        <p className="text-[10px] text-[#555] mt-0.5">{template.body_template}</p>
      </div>

      <div className="flex items-center gap-2 text-[10px] text-[#444] mb-3">
        <Clock size={11} /> Cooldown: {template.cooldown_hours}h
        <span className="mx-1 text-[#222]">|</span>
        Priority: <span className="text-[#888]">{template.priority}</span>
      </div>

      {previewData && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-3">
          <div className="bg-[#07070c] rounded-md p-2.5 border border-white/[0.04]">
            <div className="flex items-center gap-1.5 mb-1">
              <Users size={12} className="text-[#3B82F6]" />
              <span className="text-[12px] font-semibold text-white">{previewData.audience_size} users</span>
              <span className="text-[10px] text-[#555]">in audience</span>
            </div>
            {previewData.sample_users?.length > 0 && (
              <p className="text-[10px] text-[#444]">
                Sample: {previewData.sample_users.map(u => u.name).join(', ')}
              </p>
            )}
          </div>
        </motion.div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline"
          className="flex-1 h-8 text-[11px] bg-transparent border-white/[0.08] text-[#888] hover:text-white hover:border-white/[0.15]"
          onClick={handlePreview} disabled={previewing}
          data-testid={`preview-${template.template_id}`}>
          <Eye size={13} className="mr-1" /> {previewing ? 'Loading...' : 'Preview'}
        </Button>
        <Button size="sm"
          className="flex-1 h-8 text-[11px] text-white"
          style={{ background: template.color }}
          onClick={() => onExecute(template.template_id)} disabled={executing}
          data-testid={`execute-${template.template_id}`}>
          <PaperPlaneTilt size={13} className="mr-1" /> {executing ? 'Sending...' : 'Execute'}
        </Button>
      </div>
    </div>
  );
}

function CampaignRow({ campaign, onExecute, onDelete, executing }) {
  const statusColors = {
    active: '#10B981', paused: '#F59E0B', draft: '#6B7280',
  };
  const sc = statusColors[campaign.status] || '#6B7280';

  return (
    <div className="flex items-center gap-4 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-4 py-3 hover:border-white/[0.1] transition-colors"
      data-testid={`campaign-${campaign.id}`}>
      <div className="w-8 h-8 rounded-lg bg-[#9D4CDD]/10 flex items-center justify-center flex-shrink-0">
        <Megaphone size={15} weight="duotone" className="text-[#9D4CDD]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-[13px] font-semibold text-white truncate">{campaign.name}</h3>
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-medium flex-shrink-0"
            style={{ color: sc, borderColor: `${sc}30`, background: `${sc}10` }}>
            {campaign.status}
          </span>
        </div>
        <p className="text-[11px] text-[#555] truncate">{campaign.description}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-[13px] font-semibold text-white">{campaign.total_sent || 0}</p>
        <p className="text-[10px] text-[#444]">sent</p>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <Button size="sm" variant="outline"
          className="h-7 px-2.5 text-[10px] bg-transparent border-white/[0.08] text-[#888] hover:text-white"
          onClick={() => onExecute(campaign.id)} disabled={executing || campaign.status !== 'active'}
          data-testid={`execute-campaign-${campaign.id}`}>
          <PaperPlaneTilt size={12} className="mr-1" /> Send
        </Button>
        <Button size="sm" variant="outline"
          className="h-7 w-7 p-0 bg-transparent border-white/[0.08] text-[#555] hover:text-[#EF4444] hover:border-[#EF4444]/30"
          onClick={() => onDelete(campaign.id)}
          data-testid={`delete-campaign-${campaign.id}`}>
          <Trash size={12} />
        </Button>
      </div>
    </div>
  );
}

export default function AdminCampaigns() {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [result, setResult] = useState(null);

  // Create form state
  const [form, setForm] = useState({
    name: '', description: '', category: 'reminder_alerts',
    title_template: '', body_template: '', action_url: '/discover',
    priority: 'normal', targeting_type: 'inactive_users',
    targeting_params: {}, cooldown_hours: 72,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes, oRes] = await Promise.all([
        axios.get(`${API}/api/campaigns/templates`, { withCredentials: true }),
        axios.get(`${API}/api/campaigns`, { withCredentials: true }),
        axios.get(`${API}/api/campaigns/stats/overview`, { withCredentials: true }),
      ]);
      setTemplates(tRes.data);
      setCampaigns(cRes.data);
      setOverview(oRes.data);
    } catch (e) {
      console.error('Failed to load campaigns', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreviewTemplate = async (templateId) => {
    setPreviewing(true);
    try {
      const r = await axios.post(`${API}/api/campaigns/templates/${templateId}/preview`, {}, { withCredentials: true });
      return r.data;
    } catch (e) {
      console.error('Preview failed', e);
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecuteTemplate = async (templateId) => {
    setExecuting(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/api/campaigns/templates/${templateId}/execute`, {}, { withCredentials: true });
      setResult(r.data);
      fetchData();
    } catch (e) {
      console.error('Execute failed', e);
    }
    setExecuting(false);
  };

  const handleExecuteCampaign = async (campaignId) => {
    setExecuting(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/api/campaigns/${campaignId}/execute`, {}, { withCredentials: true });
      setResult(r.data);
      fetchData();
    } catch (e) {
      console.error('Execute failed', e);
    }
    setExecuting(false);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await axios.delete(`${API}/api/campaigns/${campaignId}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      console.error('Delete failed', e);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      await axios.post(`${API}/api/campaigns`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({
        name: '', description: '', category: 'reminder_alerts',
        title_template: '', body_template: '', action_url: '/discover',
        priority: 'normal', targeting_type: 'inactive_users',
        targeting_params: {}, cooldown_hours: 72,
      });
      fetchData();
    } catch (e) {
      console.error('Create failed', e);
    }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="campaigns-loading">
      <div className="h-7 w-56 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Campaigns</h1>
          <p className="text-[13px] text-[#444] mt-0.5">Engagement & re-engagement campaign engine</p>
        </div>
        <Button size="sm" className="h-8 text-[11px] bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white"
          onClick={() => setShowCreate(true)} data-testid="create-campaign-btn">
          <Plus size={13} className="mr-1" /> New Campaign
        </Button>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
          <StatCard icon={Megaphone} label="Campaigns" value={overview.total_campaigns} accent="#9D4CDD" sub={`${overview.active_campaigns} active`} />
          <StatCard icon={PaperPlaneTilt} label="Total Sent" value={overview.total_deliveries} accent="#3B82F6" />
          <StatCard icon={ChartLineUp} label="This Week" value={overview.deliveries_this_week} accent="#10B981" />
          <StatCard icon={Users} label="Users Reached" value={overview.unique_users_reached} accent="#F59E0B" />
          <StatCard icon={Lightning} label="Templates" value={overview.templates_available} accent="#EC4899" />
          <StatCard icon={CheckCircle} label="Ready" value={overview.active_campaigns} accent="#10B981" sub="Active now" />
        </div>
      )}

      {/* Execution Result Banner */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="mb-4 bg-[#10B981]/10 border border-[#10B981]/20 rounded-lg px-4 py-3 flex items-center justify-between"
            data-testid="execution-result">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} weight="duotone" className="text-[#10B981]" />
              <span className="text-[13px] text-[#10B981] font-medium">
                Campaign executed: {result.sent} sent, {result.skipped_cooldown} skipped (cooldown)
              </span>
              <span className="text-[11px] text-[#555]">Audience: {result.audience_size}</span>
            </div>
            <button onClick={() => setResult(null)} className="text-[#555] hover:text-white">
              <X size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[
          { id: 'templates', label: 'Templates', icon: Lightning },
          { id: 'custom', label: 'Custom Campaigns', icon: Megaphone },
        ].map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
              tab === t.id ? 'bg-[#9D4CDD]/15 text-[#9D4CDD]' : 'text-[#555] hover:text-[#999]'
            }`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={13} weight={tab === t.id ? 'duotone' : 'regular'} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Templates Tab */}
      {tab === 'templates' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map(t => (
            <TemplateCard key={t.template_id} template={t}
              onPreview={handlePreviewTemplate} onExecute={handleExecuteTemplate}
              previewing={previewing} executing={executing} />
          ))}
        </div>
      )}

      {/* Custom Campaigns Tab */}
      {tab === 'custom' && (
        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <div className="text-center py-12 text-[#444]">
              <Megaphone size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-[13px]">No custom campaigns yet</p>
              <p className="text-[11px] text-[#333]">Create your first campaign to get started</p>
            </div>
          ) : (
            campaigns.map(c => (
              <CampaignRow key={c.id} campaign={c}
                onExecute={handleExecuteCampaign} onDelete={handleDeleteCampaign}
                executing={executing} />
            ))
          )}
        </div>
      )}

      {/* Create Campaign Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowCreate(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0c0c14] border border-white/[0.08] rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()} data-testid="create-campaign-modal">
              <div className="sticky top-0 bg-[#0c0c14] border-b border-white/[0.06] px-5 py-3 flex items-center justify-between z-10">
                <h2 className="text-[15px] font-bold text-white">Create Campaign</h2>
                <button onClick={() => setShowCreate(false)} className="text-[#555] hover:text-white"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:border-[#9D4CDD]/40 focus:outline-none"
                    placeholder="e.g., Weekend Player Boost" data-testid="campaign-name-input" />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Description</label>
                  <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:border-[#9D4CDD]/40 focus:outline-none"
                    placeholder="Brief description of this campaign" data-testid="campaign-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Category</label>
                    <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                      className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                      data-testid="campaign-category-select">
                      {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Priority</label>
                    <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                      className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                      data-testid="campaign-priority-select">
                      {['low', 'normal', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Title Template</label>
                  <input value={form.title_template} onChange={e => setForm({ ...form, title_template: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:border-[#9D4CDD]/40 focus:outline-none"
                    placeholder="Use {{name}} for user name" data-testid="campaign-title-input" />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Body Template</label>
                  <textarea value={form.body_template} onChange={e => setForm({ ...form, body_template: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:border-[#9D4CDD]/40 focus:outline-none resize-none h-20"
                    placeholder="Use {{name}}, {{count}}, etc. for dynamic content" data-testid="campaign-body-input" />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Targeting</label>
                  <select value={form.targeting_type} onChange={e => setForm({ ...form, targeting_type: e.target.value })}
                    className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                    data-testid="campaign-targeting-select">
                    {TARGETING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Action URL</label>
                    <input value={form.action_url} onChange={e => setForm({ ...form, action_url: e.target.value })}
                      className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                      placeholder="/discover" data-testid="campaign-action-input" />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1 block">Cooldown (hours)</label>
                    <input type="number" value={form.cooldown_hours} onChange={e => setForm({ ...form, cooldown_hours: parseInt(e.target.value) || 72 })}
                      className="w-full bg-[#07070c] border border-white/[0.08] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none"
                      data-testid="campaign-cooldown-input" />
                  </div>
                </div>
              </div>
              <div className="sticky bottom-0 bg-[#0c0c14] border-t border-white/[0.06] px-5 py-3 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowCreate(false)}
                  className="h-8 text-[11px] bg-transparent border-white/[0.08] text-[#888]">Cancel</Button>
                <Button size="sm" onClick={handleCreateCampaign}
                  className="h-8 text-[11px] bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white"
                  disabled={!form.name || !form.title_template || !form.body_template}
                  data-testid="submit-campaign-btn">
                  <Plus size={13} className="mr-1" /> Create Campaign
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
